import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaBars, FaTimes, FaCalendarAlt, FaUserCircle } from "react-icons/fa";
import { ChevronDown, Scale, Heart, ShieldCheck, Pill, Sparkles, Wind, MessageSquare, UserPlus, Scissors, LogOut } from "lucide-react";
import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import UnifiedConfirmDialog from './UnifiedConfirmDialog';
import { useAuth } from "../context/AuthContext";

interface NavbarProps {
  ipAllowed: boolean;
  onLoginClick?: () => void;
  onContactClick?: () => void;
  simple?: boolean;
  // ナビゲーション前に呼ばれるガード。提供された場合、実際の navigate は渡した fn を呼ぶ側が決定する
  onNavigateAttempt?: (nav: () => void) => void;
}

const colors = {
  blue: "#2E7FA5",
  blueLight: "#8ECAD6",
  pink: "#F28A96",
  bgSoft: "#E5F4F7",
  text: "#333333",
};

const Navbar: React.FC<NavbarProps> = ({ ipAllowed, onLoginClick, onContactClick, simple = false, onNavigateAttempt }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  // onNavigateAttempt が設定されている場合はガード経由でナビゲート
  const doNavigate = (fn: () => void) => {
    if (onNavigateAttempt) { onNavigateAttempt(fn); } else { fn(); }
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);

  const changeLang = (lang: "ja" | "zh") => {
    i18n.changeLanguage(lang);
  };
  const currentLang = (i18n.language as "ja" | "zh") || "ja";

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    logout();
    setMenuOpen(false);
    setShowLogoutConfirm(false);
    navigate("/lp");
  };

  const handleContactClick = () => {
    if (onContactClick) {
      onContactClick();
    } else {
      navigate("/contact");
    }
    setMenuOpen(false);
  };

  const goHomeTop = () => {
    if (location.pathname === "/" || location.pathname === "/lp") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      doNavigate(() => navigate("/lp"));
    }
  };

  const scrollToHash = (hash: string) => {
    if (location.pathname !== "/" && location.pathname !== "/lp") {
      doNavigate(() => navigate("/lp" + hash));
      return;
    }
    const el = document.querySelector(hash);
    if (el) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = el.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (servicesRef.current && !servicesRef.current.contains(target)) setServicesOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const serviceCards = t('lp.services.cards', { returnObjects: true }) as any[];
  const services = (serviceCards || []).map((card: any) => {
    let icon = <Heart className="w-5 h-5" />;
    let color = "#F28A96"; // Default Pink
    let isDisabled = false;

    if (card.imageKey === 'dietman') { 
      icon = <Scale className="w-5 h-5" />; 
      color = "#2E7FA5"; // Male Blue
    } else if (card.imageKey === 'diet') { 
      icon = <Heart className="w-5 h-5" />; 
      color = "#F28A96"; // Female Pink
    } else if (card.imageKey === 'skincare') { 
      icon = <Sparkles className="w-5 h-5" />; 
      color = "#F28A96"; // Female Pink (Medical Beauty)
    } else if (card.imageKey === 'ed') { 
      icon = <ShieldCheck className="w-5 h-5" />; 
      color = "#2E7FA5"; // Male Blue
    } else if (card.imageKey === 'agaman') { 
      icon = <Scissors className="w-5 h-5" />; 
      color = "#2E7FA5"; // Male Blue
      isDisabled = true;
    } else if (card.imageKey === 'hair') { 
      icon = <UserPlus className="w-5 h-5" />; 
      color = "#F28A96"; // Female Pink
      isDisabled = true;
    } else if (card.imageKey === 'wellness') { 
      icon = <Pill className="w-5 h-5" />; 
      color = "#F28A96"; // Female Pink
    }

    return {
      title: card.title,
      id: card.imageKey,
      link: card.detailLink || `/menu/${card.imageKey}`,
      icon,
      color,
      disabled: isDisabled
    };
  }).filter(s => !s.disabled);

  return (
    <nav className="fixed top-0 left-0 right-0 z-[100] border-b border-border/40 bg-background/90 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 lg:px-6">
        <div className="flex items-center justify-between py-2 sm:py-3.5">
          {/* Logo */}
          <button onClick={goHomeTop} className="flex items-center transition-transform active:scale-95">
            <img src="/images/Melodislogo.png" className="h-9 w-auto md:h-11" alt="Melodis Logo" />
          </button>

          {/* Desktop Nav - Only shown if not simple */}
          {!simple && (
            <div className="hidden lg:flex items-center gap-6 xl:gap-8 text-[16px] font-medium">
              <button onClick={goHomeTop} className="text-gray-600 hover:text-coral transition-colors">
                {t("navbar.home") || "TOP"}
              </button>

              <div 
                className="relative" 
                ref={servicesRef}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <button className="text-gray-600 hover:text-coral transition-colors flex items-center gap-1">
                  {t("navbar.services") || "診療メニュー"}
                  <ChevronDown className={classNames("w-4 h-4 transition-transform", servicesOpen && "rotate-180")} />
                </button>
                <div className={classNames(
                  "absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[540px] transition-all duration-200",
                  servicesOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-2 invisible"
                )}>
                  <div className="grid grid-cols-2 gap-3 p-4 bg-white rounded-2xl shadow-xl border border-gray-100">
                    {services.map((s, i) => (
                      <Link
                        key={i}
                        to={s.disabled ? "#" : s.link}
                        className={classNames(
                          "flex items-center justify-between p-3 rounded-xl border transition-all",
                          s.disabled ? "opacity-50 grayscale cursor-not-allowed bg-gray-50/50" : "hover:border-coral/30 hover:bg-coral/[0.02] hover:shadow-sm"
                        )}
                        style={{ borderColor: s.color + "33" }}
                      >
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: s.color + "15", color: s.color }}>
                            {s.icon}
                          </span>
                          <span className="text-[14px] text-gray-700 font-bold">{s.title}</span>
                        </div>
                        {s.disabled && (
                          <span className="text-[10px] bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter">Coming Soon</span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <button onClick={() => doNavigate(() => navigate("/faq"))} className="text-gray-600 hover:text-coral transition-colors">
                {t("navbar.faq") || "よくある質問"}
              </button>

              {isAuthenticated && (
                <button onClick={() => doNavigate(() => navigate("/reserve"))} className="text-gray-600 hover:text-coral transition-colors">
                  {t("navbar.reserve") || "診療予約"}
                </button>
              )}
            </div>
          )}

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Language Switcher - Interactive Toggle */}
            <div className="relative flex items-center rounded-full border border-border bg-gray-50/50 p-1 shadow-inner h-9 w-[150px]">
              {/* Sliding Background */}
              <div 
                className={classNames(
                  "absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-full shadow-sm border border-gray-100 transition-all duration-300 ease-out",
                  currentLang === "ja" ? "left-1" : "left-[75px]"
                )}
              />
              <button
                onClick={() => changeLang("ja")}
                className={classNames(
                  "relative z-10 w-1/2 h-full text-[11px] font-bold transition-colors duration-200",
                  currentLang === "ja" ? "text-coral" : "text-gray-400 hover:text-gray-600"
                )}
              >
                日本語
              </button>
              <button
                onClick={() => changeLang("zh")}
                className={classNames(
                  "relative z-10 w-1/2 h-full text-[11px] font-bold transition-colors duration-200",
                  currentLang === "zh" ? "text-coral" : "text-gray-400 hover:text-gray-600"
                )}
              >
                中文
              </button>
            </div>

            {ipAllowed && (
              <div className="flex items-center gap-2.5">
                {isAuthenticated ? (
                  <div className="flex items-center gap-2.5">
                    <button
                      onClick={() => doNavigate(() => navigate("/mypage"))}
                      className="rounded-full bg-gradient-primary px-6 py-2 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <FaUserCircle className="w-4 h-4" />
                        <span>{t("navbar.mypage") || "マイページ"}</span>
                      </div>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="rounded-full border border-coral/30 bg-white px-5 py-2 text-sm font-bold text-coral shadow-soft transition-all hover:bg-coral/[0.03] hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
                    >
                      {t("navbar.logout")}
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2.5">
                    <button
                      onClick={onLoginClick || (() => navigate("/login"))}
                      className="rounded-full bg-gradient-primary px-5 py-2 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <span>{t("lp.header.line")}</span>
                      </div>
                    </button>
                    <button
                      onClick={handleContactClick}
                      className="rounded-full border border-coral px-5 py-1.5 text-sm font-bold text-coral bg-white shadow-soft transition-all hover:bg-coral/5 hover:-translate-y-0.5 active:scale-95 whitespace-nowrap"
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>{t("navbar.contact")}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-2">
             {/* Mobile Language Switcher - Compact Toggle */}
             <div 
              onClick={() => changeLang(currentLang === "ja" ? "zh" : "ja")}
              className="relative flex items-center rounded-full border border-border bg-gray-50/50 p-0.5 h-7 w-[64px] cursor-pointer"
            >
              <div 
                className={classNames(
                  "absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] bg-white rounded-full shadow-xs transition-all duration-300",
                  currentLang === "ja" ? "left-0.5" : "left-[calc(50%)]"
                )}
              />
              <span className={classNames("relative z-10 flex-1 text-center text-[9px] font-bold", currentLang === "ja" ? "text-coral" : "text-gray-400")}>日</span>
              <span className={classNames("relative z-10 flex-1 text-center text-[9px] font-bold", currentLang === "zh" ? "text-coral" : "text-gray-400")}>中</span>
            </div>

            {/* Conditionally show CTAs or Hamburger */}
            {(simple && !isAuthenticated) ? (
              <div className="flex items-center gap-1.5 ml-1">
                <button
                  onClick={onLoginClick || (() => navigate("/login"))}
                  className="rounded-full bg-gradient-primary px-3 py-1.5 text-[10px] font-bold text-white shadow-md active:scale-95 whitespace-nowrap"
                >
                  {t("lp.header.line") || "LINEで連絡"}
                </button>
                <button
                  onClick={handleContactClick}
                  className="rounded-full border border-coral px-3 py-1.5 text-[10px] font-bold text-coral bg-white shadow-soft active:scale-95 whitespace-nowrap"
                >
                  <MessageSquare className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors ml-1"
              >
                {menuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={classNames(
        "lg:hidden fixed inset-x-0 top-[52px] sm:top-[66px] bg-background border-b border-border/40 transition-all duration-300 z-50 overflow-y-auto",
        menuOpen ? "max-h-[calc(100vh-66px)] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="p-4 space-y-3 pb-8">
          <button onClick={() => { goHomeTop(); setMenuOpen(false); }} className="block w-full text-left p-2.5 font-bold text-gray-700 hover:bg-gray-50 rounded-xl">{t("navbar.home")}</button>
          
          <div className="space-y-1.5">
             <div className="px-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-loose">{t("navbar.services")}</div>
             <div className="grid grid-cols-2 gap-2">
                {services.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => { if (!s.disabled) { doNavigate(() => { navigate(s.link); setMenuOpen(false); }); } }}
                    className={classNames(
                      "flex flex-col items-center gap-1.5 p-2.5 rounded-xl border text-center transition-all active:scale-95",
                      s.disabled ? "opacity-50 grayscale cursor-not-allowed bg-gray-50/50" : "bg-white shadow-xs"
                    )}
                    style={{ borderColor: s.color + "22" }}
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: s.color + "15", color: s.color }}>{s.icon}</span>
                    <span className="text-[12px] font-bold text-gray-700 leading-tight">{s.title}</span>
                  </button>
                ))}
             </div>
          </div>

          <button onClick={() => doNavigate(() => { navigate("/faq"); setMenuOpen(false); })} className="block w-full text-left p-2.5 font-bold text-gray-700 hover:bg-gray-50 rounded-xl">{t("navbar.faq")}</button>

          {ipAllowed && (
            <div className="pt-3 border-t border-gray-100 space-y-2.5">
              {isAuthenticated ? (
                <div className="space-y-2.5">
                  <button onClick={() => doNavigate(() => { navigate("/reserve"); setMenuOpen(false); })} className="w-full flex items-center justify-center gap-2 rounded-xl bg-orange-500 py-3.5 font-bold text-white shadow-md active:scale-95"><FaCalendarAlt /><span>{t("navbar.reserve") || "診療予約"}</span></button>
                  <button onClick={() => doNavigate(() => { navigate("/mypage"); setMenuOpen(false); })} className="w-full flex items-center justify-center gap-2 rounded-xl bg-gradient-primary py-3.5 font-bold text-white shadow-md active:scale-95"><FaUserCircle /><span>{t("navbar.mypage")}</span></button>
                  <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 rounded-xl border border-coral bg-white py-3 font-bold text-coral shadow-soft active:scale-95">{t("navbar.logout")}</button>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <button onClick={() => doNavigate(() => { if (onLoginClick) onLoginClick(); else navigate("/login"); setMenuOpen(false); })} className="w-full rounded-xl bg-gradient-primary py-3.5 font-bold text-white shadow-md active:scale-95">{t("lp.header.line")}</button>
                  <button onClick={handleContactClick} className="w-full rounded-xl border border-coral bg-white py-3.5 font-bold text-coral shadow-soft active:scale-95">{t("navbar.contact")}</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <UnifiedConfirmDialog
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={confirmLogout}
        type="logout"
        title={t('auth.logout_confirm') || t('common.logout') || 'ログアウト'}
        message={t('auth.logout_hint') || (currentLang === 'zh' ? '您需要重新登录才能访问您的个人中心' : 'マイページにアクセスするには再ログインが必要になります')}
      />
    </nav>
  );
};

export default Navbar;
